import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-plan-card.css?inline';
import { humanizeKey, parseJsonObject } from '../../lib/portal-data.js';
import '../pk-card/pk-card.js';
import '../pk-meter/pk-meter.js';

/** Usage as a share of the limit, 0–100 (rounded); null when the limit is not a positive number. */
export function usagePercent(used: unknown, limit: unknown): number | null {
    const u = Number(used);
    const l = Number(limit);
    if (!Number.isFinite(u) || !Number.isFinite(l) || l <= 0) return null;
    return Math.min(100, Math.max(0, Math.round((u / l) * 100)));
}

/** Meter tone for a usage percentage: `warn` from 90 %, `info` from 75 %, else `success`. */
export function usageTone(percent: number): 'success' | 'info' | 'warn' {
    if (percent >= 90) return 'warn';
    if (percent >= 75) return 'info';
    return 'success';
}

const TIER_LABELS: Record<string, string> = { free: 'Free', pro: 'Pro', enterprise: 'Enterprise' };

/**
 * `<pk-plan-card>` — the tenant's plan: tier, each limit with a usage bar, and an upgrade call to action.
 *
 * Read-only: tier, limits and usage come from the product API (`GET /tenants/{id}`; contract `Tenant`).
 * Payments are not part of the kit — the CTA is a link or the product's own slotted content.
 *
 * Attributes:
 *   - `tier`          — `free` | `pro` | `enterprise` or a product tier name; shown as the heading.
 *   - `tenant-name`   — shown under the heading.
 *   - `limits`        — JSON object, e.g. `{"users":25,"systems":10}` (or the `limits` property).
 *   - `usage`         — JSON object with the same keys (or the `usage` property). A usage key with no
 *                       limit is shown as a plain count.
 *   - `upgrade-url`   — when set, a default "Upgrade" link renders in the CTA area.
 *   - `upgrade-label` — the link text (default `Upgrade plan`).
 *
 * Slots:
 *   - `cta` — replaces the default upgrade link (e.g. a "Contact sales" button).
 */
export class PkPlanCard extends HTMLElement {
    static readonly tagName = 'pk-plan-card';

    static get observedAttributes(): string[] {
        return ['tier', 'tenant-name', 'limits', 'usage', 'upgrade-url', 'upgrade-label'];
    }

    private readonly _root: ShadowRoot;
    private _limits: Record<string, unknown> | null = null;
    private _usage: Record<string, unknown> | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._render();
    }

    attributeChangedCallback(name: string): void {
        if (name === 'limits') this._limits = null;
        if (name === 'usage') this._usage = null;
        if (this.isConnected) this._render();
    }

    /** Limits by key (property wins over the attribute once set). */
    get limits(): Record<string, unknown> {
        return this._limits ?? parseJsonObject(this.getAttribute('limits'));
    }

    set limits(value: Record<string, unknown>) {
        this._limits = value ?? {};
        if (this.isConnected) this._render();
    }

    /** Usage by key (property wins over the attribute once set). */
    get usage(): Record<string, unknown> {
        return this._usage ?? parseJsonObject(this.getAttribute('usage'));
    }

    set usage(value: Record<string, unknown>) {
        this._usage = value ?? {};
        if (this.isConnected) this._render();
    }

    private _rows(): TemplateResult[] {
        const limits = this.limits;
        const usage = this.usage;
        const keys = [...Object.keys(limits), ...Object.keys(usage).filter((k) => !(k in limits))];
        return keys.map((key) => {
            const used = usage[key];
            const limit = limits[key];
            const pct = usagePercent(used ?? 0, limit);
            const usedText = used === undefined || used === null ? '—' : String(used);
            return html`
                <div class="row" data-key=${key}>
                    <div class="row-head">
                        <span class="row-label">${humanizeKey(key)}</span>
                        <span class="row-value">${limit === undefined || limit === null ? usedText : `${usedText} / ${String(limit)}`}</span>
                    </div>
                    ${pct === null ? '' : html`<pk-meter value=${String(pct)} tone=${usageTone(pct)} label=${`${pct}%`}></pk-meter>`}
                </div>
            `;
        });
    }

    private template(): TemplateResult {
        const tier = (this.getAttribute('tier') ?? '').trim();
        const tierLabel = TIER_LABELS[tier.toLowerCase()] ?? (tier || 'No plan');
        const tenantName = this.getAttribute('tenant-name') ?? '';
        const upgradeUrl = this.getAttribute('upgrade-url') ?? '';
        const upgradeLabel = this.getAttribute('upgrade-label') ?? 'Upgrade plan';
        const rows = this._rows();

        return html`
            <style>${styles}</style>
            <pk-card>
                <span slot="eyebrow" class="eyebrow">Plan</span>
                <h2 class="tier" data-tier=${tier.toLowerCase()}>${tierLabel}</h2>
                ${tenantName ? html`<p class="tenant">${tenantName}</p>` : ''}
                ${rows.length > 0 ? html`<div class="rows">${rows}</div>` : html`<p class="no-limits">No limits on this plan.</p>`}
                <div class="cta">
                    <slot name="cta">
                        ${upgradeUrl ? html`<a class="upgrade" href=${upgradeUrl}>${upgradeLabel}</a>` : ''}
                    </slot>
                </div>
            </pk-card>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkPlanCard.tagName)) {
    customElements.define(PkPlanCard.tagName, PkPlanCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-plan-card': PkPlanCard;
    }
}
